'use client';

import { useMutation } from '@tanstack/react-query';
import { FieldArray, Form, Formik } from 'formik';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import Select from 'react-select';
import {
  CheckBox,
  SelectBoxWithSearch,
  TextArea,
  TextInput,
} from '../../components/Form/Form';
import BreadCrumb from '../../components/template/BreadCrumb';
import Header from '../../components/template/Header';
import api from '../../domain/api';
import {
  LINK_URL,
  PAGE_SINGLE_TITLE,
  PAGE_TITLE,
} from '../../shared/enums/activitys_enum';
import { useGetDropdownOptions } from '../../shared/hooks/UseActivity';
import { useSelectAllProcess } from '../../shared/hooks/UseProcess';
import { uploadGallery } from '../../shared/upload';

import './add-multiple-activity.css';
import './step-flow.css';

const DUE_TIME_FORMAT = (date = new Date()) => {
  // Format: yyyy-MM-ddTHH:mm (for datetime-local)
  const pad = (n) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const AddMultipleActivity = () => {
  const history = useHistory();
  const [process_data] = useSelectAllProcess();
  const { all_processs } = process_data;

  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePicked, setImagePicked] = useState(null);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [baseFormData, setBaseFormData] = useState(null);
  const [combinations, setCombinations] = useState([
    { id: 1, task_name: '', client: '' },
  ]);

  console.log('featuredImage', featuredImage);
  console.log('imagePicked', imagePicked);

  // Utility functions for combinations
  const addCombination = () => {
    const newId = Math.max(...combinations.map((c) => c.id)) + 1;
    setCombinations([
      ...combinations,
      { id: newId, task_name: '', client: '' },
    ]);
  };

  const removeCombination = (id) => {
    if (combinations.length > 1) {
      setCombinations(combinations.filter((c) => c.id !== id));
    }
  };

  const updateCombination = (id, field, value) => {
    setCombinations(
      combinations.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const goToStep2 = (formData) => {
    setBaseFormData(formData);
    setCurrentStep(2);
  };

  const goBackToStep1 = () => {
    setCurrentStep(1);
  };

  const pickImage = ({ field, value }) => {
    const imagesCollection = imagePicked ? imagePicked : {};
    imagesCollection[field] = value;
    setImagePicked(imagesCollection);
    setFeaturedImage(imagesCollection);

    // Create previews for attachments
    if (field === 'attachments' && value) {
      const files = Array.from(value);
      const previews = files.map((file, index) => ({
        id: Date.now() + index,
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null,
      }));
      setAttachmentPreviews(previews);
    }
  };

  const activityMutation = useMutation({
    mutationFn: async (data) => {
      const resp = await api.post(`/activitys`, data);
      return resp.data;
    },

    onSuccess() {
      toast.success('Activity created successfully!');
      history.push(`/${LINK_URL}`);
    },
    onError(error) {
      toast.error('Failed to create activity: ' + error.message);
    },
  });

  const submitFormClicked = async (baseData) => {
    const uploadedFiles = await uploadGallery(imagePicked?.attachments);

    // Generate activities based on combinations
    const activities = combinations.map((combo) => ({
      ...baseData,
      task_name: combo.task_name,
      client: combo.client,
      attachments: uploadedFiles,
    }));

    // Send one by one
    for (const activity of activities) {
      await activityMutation.mutateAsync(activity);
    }
  };

  const [dropdownOptions] = useGetDropdownOptions();

  return (
    <>
      <div className='pace-done'>
        <div>
          <Header />
          <BreadCrumb
            title={`Add ${PAGE_SINGLE_TITLE}`}
            mainLinkTitle={PAGE_TITLE}
            mainLinkUrl={LINK_URL}
            activeLink='Add'
          />
        </div>

        <div className='enhanced-activity-container'>
          <div className='container-fluid'>
            {/* Step Progress Indicator */}
            <div className='step-progress-container'>
              <div className='step-progress'>
                <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                  <div className='step-circle'>
                    <i className='fa fa-cog'></i>
                  </div>
                  <div className='step-label'>Base Configuration</div>
                </div>
                <div className='step-line'></div>
                <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                  <div className='step-circle'>
                    <i className='fa fa-tasks'></i>
                  </div>
                  <div className='step-label'>Task & Client Combinations</div>
                </div>
              </div>
            </div>

            {currentStep === 1 ? (
              /* Step 1: Base Form */
              <Formik
                initialValues={{
                  assigned_to: '',
                  activity_cycle: '',
                  due_time: '',
                  process: '',
                  sop: '',
                  checklist: [],
                  estimated_time: '',
                  actual_time: '',
                  task_description: '',
                  attachments: '',
                  review_status: '',
                  activity_status: '',
                  comments: '',
                }}
                validationSchema={Yup.object({
                  assigned_to: Yup.string().required('Required'),
                  due_time: Yup.string().required('Required'),
                  process: Yup.string().required('Required'),
                  review_status: Yup.string().required('Required'),
                  activity_status: Yup.string().required('Required'),
                })}
                onSubmit={(values) => {
                  // Check if activity status is COMPLETED and validate checklist
                  const selectedActivityStatus =
                    dropdownOptions?.activity_status?.find(
                      (status) => status.value === values.activity_status
                    );

                  if (selectedActivityStatus?.label === 'COMPLETED') {
                    // Check if there are checklist items
                    if (values.checklist && values.checklist.length > 0) {
                      // Check if all checklist items are checked
                      const allChecked = values.checklist.every(
                        (item) => item.is_checked
                      );
                      if (!allChecked) {
                        toast.error(
                          'All checklist items must be completed when status is COMPLETED'
                        );
                        return;
                      }
                    }
                  }

                  goToStep2(values);
                }}
              >
                {(formik) => (
                  <Form>
                    <div className='row'>
                      <div className='col-md-8'>
                        <div className='enhanced-form-card'>
                          <div
                            className='enhanced-form-header'
                            style={{ color: 'white', padding: '15px 20px' }}
                          >
                            <h2
                              className='enhanced-form-title'
                              style={{
                                color: 'white',
                                fontSize: '18px',
                                margin: '0 0 5px 0',
                              }}
                            >
                              <i className='fa fa-cog me-2'></i>
                              Step 1: Base Configuration
                            </h2>
                            <p
                              className='enhanced-form-subtitle'
                              style={{ margin: 0, fontSize: '13px' }}
                            >
                              Set up the common details that will apply to all
                              activities
                            </p>
                          </div>

                          <div
                            className='enhanced-form-body'
                            style={{ padding: '15px 20px' }}
                          >
                            {/* Basic Information Section */}
                            <div
                              className='enhanced-form-section'
                              style={{ marginBottom: '15px' }}
                            >
                              <h3
                                className='enhanced-section-title'
                                style={{
                                  fontSize: '14px',
                                  marginBottom: '10px',
                                }}
                              >
                                <div className='enhanced-section-icon'>
                                  <i className='fa fa-info'></i>
                                </div>
                                Basic Information
                              </h3>
                              <div className='row' style={{ margin: '0 -5px' }}>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SelectBoxWithSearch
                                    label='Assigned to'
                                    name='assigned_to'
                                    options={
                                      dropdownOptions?.assigned_to?.map(
                                        (item) => ({
                                          value: item.value,
                                          label: item.label,
                                        })
                                      ) || []
                                    }
                                  />
                                </div>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SelectBoxWithSearch
                                    label='Activity Cycle'
                                    name='activity_cycle'
                                    options={
                                      dropdownOptions?.activity_cycle?.map(
                                        (item) => ({
                                          value: item.value,
                                          label: item.label,
                                        })
                                      ) || []
                                    }
                                  />
                                </div>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <div style={{ position: 'relative' }}>
                                    <TextInput
                                      label='Due Time'
                                      name='due_time'
                                      type='datetime-local'
                                      placeholder='Select due date and time'
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <button
                                        type='button'
                                        className='btn btn-xs btn-today-quick'
                                        style={{
                                          background:
                                            'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: 4,
                                          fontSize: 10,
                                          padding: '2px 8px',
                                          fontWeight: 600,
                                          boxShadow:
                                            '0 1px 4px rgba(220,38,38,0.08)',
                                          textAlign: 'right',
                                        }}
                                        onClick={() => {
                                          formik.setFieldValue(
                                            'due_time',
                                            DUE_TIME_FORMAT()
                                          );
                                        }}
                                        title='Set to now'
                                      >
                                        Today
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SelectBoxWithSearch
                                    label='Process'
                                    name='process'
                                    placeholder='Select process'
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        'process',
                                        e.target.value
                                      );
                                      formik.setFieldValue('checklist', []);

                                      if (all_processs) {
                                        const filteredData =
                                          all_processs.filter(
                                            (item, index) =>
                                              item._id === e.target.value
                                          );

                                        if (
                                          filteredData &&
                                          filteredData.length > 0
                                        ) {
                                          const checklistData =
                                            filteredData[0].checklist;
                                          if (
                                            checklistData &&
                                            checklistData.length > 0
                                          ) {
                                            const checklistDataMapped =
                                              checklistData.map((item) => ({
                                                name: item.name,
                                                is_checked: false,
                                              }));
                                            formik.setFieldValue(
                                              'checklist',
                                              checklistDataMapped
                                            );
                                          }
                                        }
                                      }
                                    }}
                                    options={dropdownOptions?.process?.map(
                                      (item) => ({
                                        value: item.value,
                                        label: item.label,
                                      })
                                    )}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Time Tracking Section */}
                            <div
                              className='enhanced-form-section'
                              style={{ marginBottom: '15px' }}
                            >
                              <h3
                                className='enhanced-section-title'
                                style={{
                                  fontSize: '14px',
                                  marginBottom: '10px',
                                }}
                              >
                                <div className='enhanced-section-icon'>
                                  <i className='fa fa-clock'></i>
                                </div>
                                Time Tracking
                              </h3>
                              <div className='row' style={{ margin: '0 -5px' }}>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <label
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '13px',
                                    }}
                                  >
                                    Estimated Time (minutes)
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <TextInput
                                      name='estimated_time'
                                      type='number'
                                      placeholder='Enter estimated time'
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 4,
                                        marginTop: 3,
                                      }}
                                    >
                                      {[30, 45, 60, 100].map((val) => (
                                        <button
                                          key={val}
                                          type='button'
                                          className={`btn btn-xs btn-chip-quick ${
                                            formik.values.estimated_time ===
                                            val.toString()
                                              ? 'btn-chip-active'
                                              : ''
                                          }`}
                                          style={{
                                            background:
                                              formik.values.estimated_time ===
                                              val.toString()
                                                ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                                : '#f3f4f6',
                                            color:
                                              formik.values.estimated_time ===
                                              val.toString()
                                                ? 'white'
                                                : '#374151',
                                            border: 'none',
                                            borderRadius: 4,
                                            fontSize: 10,
                                            padding: '2px 8px',
                                            fontWeight: 600,
                                            boxShadow:
                                              formik.values.estimated_time ===
                                              val.toString()
                                                ? '0 1px 4px rgba(220,38,38,0.18)'
                                                : 'none',
                                          }}
                                          onClick={() =>
                                            formik.setFieldValue(
                                              'estimated_time',
                                              val.toString()
                                            )
                                          }
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <label
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '13px',
                                    }}
                                  >
                                    Actual Time (minutes)
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <TextInput
                                      name='actual_time'
                                      type='number'
                                      placeholder='Enter actual time'
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 4,
                                        marginTop: 3,
                                      }}
                                    >
                                      {[30, 45, 60, 100].map((val) => (
                                        <button
                                          key={val}
                                          type='button'
                                          className={`btn btn-xs btn-chip-quick ${
                                            formik.values.actual_time ===
                                            val.toString()
                                              ? 'btn-chip-active'
                                              : ''
                                          }`}
                                          style={{
                                            background:
                                              formik.values.actual_time ===
                                              val.toString()
                                                ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                                : '#f3f4f6',
                                            color:
                                              formik.values.actual_time ===
                                              val.toString()
                                                ? 'white'
                                                : '#374151',
                                            border: 'none',
                                            borderRadius: 4,
                                            fontSize: 10,
                                            padding: '2px 8px',
                                            fontWeight: 600,
                                            boxShadow:
                                              formik.values.actual_time ===
                                              val.toString()
                                                ? '0 1px 4px rgba(16,185,129,0.18)'
                                                : 'none',
                                          }}
                                          onClick={() =>
                                            formik.setFieldValue(
                                              'actual_time',
                                              val.toString()
                                            )
                                          }
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Status Section */}
                            <div
                              className='enhanced-form-section'
                              style={{ marginBottom: '15px' }}
                            >
                              <h3
                                className='enhanced-section-title'
                                style={{
                                  fontSize: '14px',
                                  marginBottom: '10px',
                                }}
                              >
                                <div className='enhanced-section-icon'>
                                  <i className='fa fa-flag'></i>
                                </div>
                                Status & Review
                              </h3>
                              <div className='row' style={{ margin: '0 -5px' }}>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SelectBoxWithSearch
                                    label='Review Status'
                                    name='review_status'
                                    options={
                                      dropdownOptions?.review_status?.map(
                                        (item) => ({
                                          value: item.value,
                                          label: item.label,
                                        })
                                      ) || []
                                    }
                                  />
                                </div>
                                <div
                                  className='col-md-6'
                                  style={{
                                    padding: '0 5px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <label
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '13px',
                                    }}
                                  >
                                    Activity Status
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <SelectBoxWithSearch
                                      name='activity_status'
                                      placeholder='Select activity status'
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          'activity_status',
                                          e.target.value
                                        );

                                        // Auto-check all checklist items if COMPLETED is selected
                                        const selectedStatus =
                                          dropdownOptions?.activity_status?.find(
                                            (status) =>
                                              status.value === e.target.value
                                          );

                                        if (
                                          selectedStatus?.label ===
                                            'COMPLETED' &&
                                          formik.values.checklist?.length > 0
                                        ) {
                                          const allCheckedList =
                                            formik.values.checklist.map(
                                              (item) => ({
                                                ...item,
                                                is_checked: true,
                                              })
                                            );
                                          formik.setFieldValue(
                                            'checklist',
                                            allCheckedList
                                          );
                                          toast.success(
                                            'All checklist items have been automatically checked for COMPLETED status'
                                          );
                                        }
                                      }}
                                      options={
                                        dropdownOptions?.activity_status?.map(
                                          (item) => ({
                                            value: item.value,
                                            label: item.label,
                                          })
                                        ) || []
                                      }
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 4,
                                        marginTop: 3,
                                      }}
                                    >
                                      {dropdownOptions?.activity_status
                                        ?.slice(0, 3)
                                        .map((item) => (
                                          <button
                                            key={item.value}
                                            type='button'
                                            className={`btn btn-xs btn-chip-quick ${
                                              formik.values.activity_status ===
                                              item.value
                                                ? 'btn-chip-active'
                                                : ''
                                            }`}
                                            style={{
                                              background:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                                  : '#f3f4f6',
                                              color:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? 'white'
                                                  : '#374151',
                                              border: 'none',
                                              borderRadius: 4,
                                              fontSize: 10,
                                              padding: '2px 8px',
                                              fontWeight: 600,
                                              boxShadow:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? '0 1px 4px rgba(220,38,38,0.18)'
                                                  : 'none',
                                            }}
                                            onClick={() => {
                                              formik.setFieldValue(
                                                'activity_status',
                                                item.value
                                              );

                                              // Auto-check all checklist items if COMPLETED is selected
                                              if (
                                                item.label === 'COMPLETED' &&
                                                formik.values.checklist
                                                  ?.length > 0
                                              ) {
                                                const allCheckedList =
                                                  formik.values.checklist.map(
                                                    (checkItem) => ({
                                                      ...checkItem,
                                                      is_checked: true,
                                                    })
                                                  );
                                                formik.setFieldValue(
                                                  'checklist',
                                                  allCheckedList
                                                );
                                                toast.success(
                                                  'All checklist items have been automatically checked for COMPLETED status'
                                                );
                                              }
                                            }}
                                          >
                                            {item.label}
                                          </button>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className='col-md-12'
                                  style={{ padding: '0 5px' }}
                                >
                                  <TextArea
                                    label='Comments'
                                    name='comments'
                                    type='text'
                                    placeholder='Enter any additional comments'
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            className='enhanced-form-actions'
                            style={{ padding: '15px 20px' }}
                          >
                            <button
                              type='submit'
                              className='enhanced-btn enhanced-btn-primary'
                              style={{ padding: '8px 16px', fontSize: '14px' }}
                            >
                              <i className='fa fa-arrow-right me-2'></i>
                              Continue to Combinations
                            </button>
                            <button
                              type='button'
                              className='enhanced-btn enhanced-btn-secondary'
                              style={{ padding: '8px 16px', fontSize: '14px' }}
                              onClick={() => history.goBack()}
                            >
                              <i className='fa fa-arrow-left me-2'></i>
                              Go Back
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='col-md-4'>
                        {/* Checklist Card */}
                        <div
                          className='enhanced-sidebar-card'
                          style={{ marginBottom: '15px' }}
                        >
                          <div
                            className='enhanced-sidebar-header'
                            style={{ padding: '12px 15px' }}
                          >
                            <h4
                              className='enhanced-sidebar-title'
                              style={{ fontSize: '14px', margin: '0 0 5px 0' }}
                            >
                              <i className='fa fa-check-square'></i>
                              Process Checklist
                            </h4>
                            <p
                              className='enhanced-sidebar-subtitle'
                              style={{ margin: 0, fontSize: '12px' }}
                            >
                              Complete all required steps
                            </p>
                            {(() => {
                              const selectedActivityStatus =
                                dropdownOptions?.activity_status?.find(
                                  (status) =>
                                    status.value ===
                                    formik.values.activity_status
                                );
                              return (
                                selectedActivityStatus?.label === 'COMPLETED' &&
                                formik.values.checklist?.length > 0 && (
                                  <div
                                    className='alert alert-warning'
                                    style={{
                                      margin: '8px 0 0 0',
                                      padding: '6px 8px',
                                      fontSize: '11px',
                                      border: '1px solid #ffc107',
                                      borderRadius: '4px',
                                      backgroundColor: '#fff3cd',
                                    }}
                                  >
                                    <i className='fa fa-exclamation-triangle me-1'></i>
                                    All items must be checked for COMPLETED
                                    status
                                  </div>
                                )
                              );
                            })()}
                          </div>
                          <div
                            className='enhanced-sidebar-body'
                            style={{ padding: '10px 15px' }}
                          >
                            {formik.values.checklist &&
                              formik.values.checklist.length > 0 && (
                                <div className='toggle-switch-wrapper'>
                                  <label className='toggle-switch'>
                                    <input
                                      type='checkbox'
                                      checked={formik.values.checklist.every(
                                        (item) => item.is_checked
                                      )}
                                      onChange={() => {
                                        const allChecked =
                                          formik.values.checklist.every(
                                            (item) => item.is_checked
                                          );
                                        const toggled =
                                          formik.values.checklist.map(
                                            (item) => ({
                                              ...item,
                                              is_checked: !allChecked,
                                            })
                                          );
                                        formik.setFieldValue(
                                          'checklist',
                                          toggled
                                        );
                                      }}
                                    />
                                    <span className='slider'></span>
                                    <span className='toggle-label'>
                                      {formik.values.checklist.every(
                                        (item) => item.is_checked
                                      )
                                        ? 'Unselect All'
                                        : 'Select All'}
                                    </span>
                                  </label>
                                </div>
                              )}
                            <FieldArray
                              name='checklist'
                              render={(arrayHelper) => (
                                <div>
                                  {formik.values.checklist &&
                                  formik.values.checklist.length > 0 ? (
                                    formik.values.checklist.map(
                                      (item, index) => (
                                        <div
                                          key={index}
                                          className='checklist-item'
                                        >
                                          <CheckBox
                                            name={`checklist[${index}].is_checked`}
                                            type='checkbox'
                                          >
                                            {item.name}
                                          </CheckBox>
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: 'center',
                                        color: '#6b7280',
                                        padding: '20px',
                                      }}
                                    >
                                      <i
                                        className='fa fa-info-circle'
                                        style={{
                                          fontSize: '24px',
                                          marginBottom: '8px',
                                        }}
                                      ></i>
                                      <p>
                                        Select a process to view checklist items
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            />
                          </div>
                        </div>

                        {/* SOP Card */}
                        <div className='enhanced-sidebar-card'>
                          <div
                            className='enhanced-sidebar-header'
                            style={{ padding: '12px 15px' }}
                          >
                            <h4
                              className='enhanced-sidebar-title'
                              style={{ fontSize: '14px', margin: '0 0 5px 0' }}
                            >
                              <i className='fa fa-book'></i>
                              Standard Operating Procedure
                            </h4>
                            <p
                              className='enhanced-sidebar-subtitle'
                              style={{ margin: 0, fontSize: '12px' }}
                            >
                              Follow these guidelines
                            </p>
                          </div>
                          <div
                            className='enhanced-sidebar-body'
                            style={{ padding: '10px 15px' }}
                          >
                            {all_processs &&
                            all_processs.filter(
                              (item) => item._id === formik.values.process
                            ) &&
                            all_processs.filter(
                              (item) => item._id === formik.values.process
                            )[0] &&
                            all_processs.filter(
                              (item) => item._id === formik.values.process
                            )[0].sop ? (
                              all_processs
                                .filter(
                                  (item) => item._id === formik.values.process
                                )[0]
                                .sop.map((item, index) => (
                                  <div key={index} className='sop-item'>
                                    <div className='sop-icon'>
                                      <i className='fa fa-list-ol'></i>
                                    </div>
                                    <div
                                      className='sop-content'
                                      dangerouslySetInnerHTML={{
                                        __html: item.name,
                                      }}
                                    />
                                  </div>
                                ))
                            ) : (
                              <div
                                style={{
                                  textAlign: 'center',
                                  color: '#6b7280',
                                  padding: '20px',
                                }}
                              >
                                <i
                                  className='fa fa-info-circle'
                                  style={{
                                    fontSize: '24px',
                                    marginBottom: '8px',
                                  }}
                                ></i>
                                <p>Select a process to view SOP guidelines</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              /* Step 2: Combinations */
              <div className='combinations-step'>
                <div className='enhanced-form-card'>
                  <div
                    className='enhanced-form-header'
                    style={{ color: 'white', padding: '15px 20px' }}
                  >
                    <h2
                      className='enhanced-form-title'
                      style={{
                        color: 'white',
                        fontSize: '18px',
                        margin: '0 0 5px 0',
                      }}
                    >
                      <i className='fa fa-tasks me-2'></i>
                      Step 2: Task & Client Combinations
                    </h2>
                    <p
                      className='enhanced-form-subtitle'
                      style={{ margin: 0, fontSize: '13px' }}
                    >
                      Add multiple combinations of tasks and clients for your
                      activities
                    </p>
                  </div>

                  <div
                    className='enhanced-form-body'
                    style={{ padding: '15px 20px' }}
                  >
                    <div className='combinations-container'>
                      {combinations.map((combo, index) => (
                        <div key={combo.id} className='combination-card'>
                          <div className='combination-header'>
                            <h4 style={{ fontSize: '14px', margin: 0 }}>
                              <i className='fa fa-layer-group me-2'></i>
                              Combination {index + 1}
                            </h4>
                            {combinations.length > 1 && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger'
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => removeCombination(combo.id)}
                              >
                                <i className='fa fa-trash'></i>
                              </button>
                            )}
                          </div>

                          <div className='row' style={{ margin: '0 -5px' }}>
                            <div
                              className='col-md-6'
                              style={{ padding: '0 5px', marginBottom: '10px' }}
                            >
                              <label
                                style={{
                                  fontWeight: 600,
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  display: 'block',
                                }}
                              >
                                Task Name
                              </label>
                              <input
                                type='text'
                                className='form-control'
                                placeholder='Enter task name'
                                value={combo.task_name}
                                onChange={(e) =>
                                  updateCombination(
                                    combo.id,
                                    'task_name',
                                    e.target.value
                                  )
                                }
                                style={{
                                  fontSize: '14px',
                                  padding: '8px 12px',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  backgroundColor: '#ffffff',
                                }}
                              />
                            </div>
                            <div
                              className='col-md-6'
                              style={{ padding: '0 5px', marginBottom: '10px' }}
                            >
                              <label
                                style={{
                                  fontWeight: 600,
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  display: 'block',
                                }}
                              >
                                Client
                              </label>
                              <Select
                                value={
                                  dropdownOptions?.client?.find(
                                    (item) => item.value === combo.client
                                  ) || null
                                }
                                onChange={(selectedOption) =>
                                  updateCombination(
                                    combo.id,
                                    'client',
                                    selectedOption?.value || ''
                                  )
                                }
                                placeholder='Select client'
                                options={
                                  dropdownOptions?.client?.map((item) => ({
                                    value: item.value,
                                    label: item.label,
                                  })) || []
                                }
                                classNamePrefix='react-select'
                                isClearable
                                isSearchable
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    fontSize: '14px',
                                    minHeight: '38px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                  }),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className='add-combination-btn-container'>
                        <button
                          type='button'
                          className='btn btn-outline-primary btn-add-combination'
                          onClick={addCombination}
                        >
                          <i className='fa fa-plus me-2'></i>
                          Add Another Combination
                        </button>
                      </div>
                    </div>

                    <div className='combinations-summary'>
                      <h4>
                        <i className='fa fa-list me-2'></i>
                        Summary
                      </h4>
                      <p>
                        You will create <strong>{combinations.length}</strong>{' '}
                        activities with the base configuration.
                      </p>
                      <div className='summary-items'>
                        {combinations.map((combo, index) => (
                          <div key={combo.id} className='summary-item'>
                            <span className='summary-index'>{index + 1}</span>
                            <span className='summary-text'>
                              {combo.task_name && combo.client
                                ? `${combo.task_name} → ${
                                    dropdownOptions?.client?.find(
                                      (c) => c.value === combo.client
                                    )?.label || combo.client
                                  }`
                                : 'Incomplete combination'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div
                    className='enhanced-form-actions'
                    style={{ padding: '15px 20px' }}
                  >
                    <button
                      type='button'
                      className='enhanced-btn enhanced-btn-primary'
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      onClick={() => {
                        const hasValidCombinations = combinations.every(
                          (c) => c.task_name && c.client
                        );
                        if (!hasValidCombinations) {
                          toast.error(
                            'Please complete all task and client combinations'
                          );
                          return;
                        }
                        submitFormClicked(baseFormData);
                      }}
                      disabled={activityMutation.isLoading}
                    >
                      {activityMutation.isLoading ? (
                        <>
                          <i className='fa fa-spinner fa-spin me-2'></i>
                          Creating Activities...
                        </>
                      ) : (
                        <>
                          <i className='fa fa-save me-2'></i>
                          Create All Activities
                        </>
                      )}
                    </button>
                    <button
                      type='button'
                      className='enhanced-btn enhanced-btn-secondary'
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      onClick={goBackToStep1}
                    >
                      <i className='fa fa-arrow-left me-2'></i>
                      Back to Configuration
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMultipleActivity;
